/* array_component.control_adapter.js */

const $ = require("jquery");
const _ = require("lodash");
const underscoreString = require("underscore.string");
_.mixin(underscoreString.exports());
const EventEmitter = require("events").EventEmitter;
const util = require("util");
const MultiComboBox = require("@gluenetworks/combobox").Multiple;
const PickList = require("@gluenetworks/picklist").PickList;
const q = require("q");

/**
 * @class ArrayTagListAdapter
 * @classdesc render a simple array as a tag list.
 * @implements AngularControlAdapter
 *
 * @param logger {Bunyan} The logger
 * @param containingElem {DomElement} The containing dom element for this control
 * @param formCtrlDef {FormControlDefinition} The definition of this control object.
 *
 * @requires util
 * @requires EventEmitter
 *
 * @fires ArrayControlAdapter#ValueChanged
 */
module.exports = function ArrayComponentAdapter(logger, containingElem, formCtrlDef) {
    var self = this;
    var loadedDeferred = q.defer();
    //call Event Emitter Constructor
    EventEmitter.call(this);
    var _scope;
    var _oldValue;
    var _options = {};
    this.displayType = "TagList";
    this._multiEditMode = _.get(formCtrlDef, "displayModeOptions.multiEditMode", false);
    this._createOption = _.get(formCtrlDef, "configSettings.Display.tagList.createOption", false);

    if (_.has(formCtrlDef, "configSettings.Display.pickList")) {
        let configOptions = {
            optionsLabel: formCtrlDef.configSettings.Display.pickList.optionsLabel,
            selectedLabel: formCtrlDef.configSettings.Display.pickList.selectedLabel,
            verticalLayout: formCtrlDef.configSettings.Display.pickList.verticalLayout,
            hideOptionFilter: formCtrlDef.configSettings.Display.pickList.hideOptionFilter,
        };
        this.arrayListComponent = new PickList(configOptions);
        this.displayType = "PickList";
    } else {
        this.arrayListComponent = new MultiComboBox({
            allowClear: true,
            createOption: this._createOption,
            multiEditOption: this._multiEditMode,
        });
    }

    /********************************************************************************/
    /*** START Type Definitions                                                   ***/
    /********************************************************************************/
    /********************************************************************************/
    /*** END Type Definitions                                                     ***/
    /*** START Private API                                                        ***/
    /********************************************************************************/

    /**
     * Fire the Value Changed Event
     * @private
     */
    this.selectValueChangedListener = function () {
        //Get the new Value
        var newVal = self.getValue();
        self.emit("ValueChanged", {
            name: formCtrlDef.name,
            containingElem: containingElem,
            controlAdapter: self,
            formControlDefinition: formCtrlDef,
            oldValue: _oldValue,
            newValue: newVal,
        });
        _oldValue = newVal;
    };

    /********************************************************************************/
    /*** END Private API                                                          ***/
    /*** START Public API                                                         ***/
    /********************************************************************************/
    //The name of the control
    this.name = formCtrlDef.name;

    /**
     * @returns the data associated with this control. When both multiEditMode and
     * createOption are on, this is {action, tagIds} instead of a plain array.
     */
    this.getValue = function () {
        let val = this.arrayListComponent.getValue();
        if (self._multiEditMode && self._createOption) {
            return {
                action: self.arrayListComponent.multiEditAction,
                tagIds: val,
            };
        }
        return val;
    };

    /**
     * Set this controls current value.
     * @param value {string} The value to set this control to.
     * @returns the value associated with this control, it might not always be the same as the value passed in for
     * example adapters with a ValueScope.PARENT.
     */
    this.setValue = function (value) {
        if (self._multiEditMode && self._createOption) {
            self.arrayListComponent.setValue(_.get(value, "tagIds", []));
            self.arrayListComponent.setMultiEditAction(_.get(value, "action", "Add"));
        } else {
            self.arrayListComponent.setValue(value);
        }
        return self.getValue();
    };

    this.getMultiEditActive = function () {
        return this._multiEditActive;
    };

    this.setMultiEditActive = function (flag) {
        if (!this._readOnlyField) {
            this.setReadOnly(!flag);
            let oldValue = this._multiEditActive;
            this._multiEditActive = flag;
            if (oldValue !== this._multiEditActive) {
                this.emit("MultiEditActive", {
                    oldValue: oldValue,
                    newValue: this._multiEditActive,
                });
            }
        }
    };

    this.setReadOnly = function (flag) {
        if (_.isFunction(this.arrayListComponent.setDisabled)) {
            this.arrayListComponent.setDisabled(flag);
        }
    };

    /**
     * Tear down this control
     */
    this.tearDown = function () {
        this.arrayListComponent.shutdown();
        //Do Nothing the dom is cleaned up, the scope is destroyed for us and any listeners
        //on self are unregistered automatically
    };

    /********************************************************************************/
    /*** END Public API                                                           ***/
    /*** Start Angular/Render Work                                                ***/
    /********************************************************************************/

    /**
     * A Hook to start the angular work, due to timing issues don't try anything angularis before this hook is called.
     * @param $scope {$scope} Angular $scope
     * @parma $templateCache {$templateCache}
     */
    this.$init = function ($scope, $templateCache, $compile, $timeout) {
        _scope = $scope;
        let options = [];
        formCtrlDef.configSettings.value.forEach((item, idx) => {
            if (_.isObject(item.value)) {
                self.illegalTagList = true;
            } else {
                let label = item.value;
                if (_.has(formCtrlDef, "configSettings.Display.pickList.labels")) {
                    if (formCtrlDef.configSettings.Display.pickList.labels[idx]) {
                        label = formCtrlDef.configSettings.Display.pickList.labels[idx];
                    }
                } else if (_.has(formCtrlDef, "configSettings.Display.tagList.labels")) {
                    if (formCtrlDef.configSettings.Display.tagList.labels[idx]) {
                        label = formCtrlDef.configSettings.Display.tagList.labels[idx];
                    }
                }
                let option = {
                    label: label,
                    value: item.value,
                };
                options.push(option);
            }
        });
        this.arrayListComponent.on(
            "Value",
            (this.selectValueChangedListener = this.selectValueChangedListener.bind(this))
        );
        if (this._multiEditMode && this._createOption) {
            this.arrayListComponent.on("MultiEditAction", this.selectValueChangedListener);
        }
        this.arrayListComponent.setOptions(options);

        //Setup directive
        let refElem;
        if (this.displayType === "PickList") {
            refElem = $('<div class="nested_rendered_controls object_control form_entry"></div>');
        } else {
            var template = $templateCache.get(
                "templatecache/templates/array_tag_list.template.html"
            );
            refElem = $(template);
        }
        containingElem.append(refElem);
        refElem.prepend(this.arrayListComponent.renderer.rootElement);

        //Notify the world this adapter is ready to be compiled/linked and values retrieved
        self.emit("InitializationComplete", {
            initializedValue: self.getValue(),
        });
        return loadedDeferred.promise;
    };
    this.$init.$inject = ["$scope", "$templateCache", "$compile", "$timeout"];

    this.$compiledAndLinked = function ($scope, $timeout) {
        if (this.displayType === "PickList") {
            if (_.has(formCtrlDef.configSettings, "Display.pickList.lineCount")) {
                this.arrayListComponent.lineCount = _.get(
                    formCtrlDef.configSettings,
                    "Display.pickList.lineCount"
                );
            }
        }

        // If this is multiEditMode, set the field as read only by default
        if (this._multiEditMode) {
            this.setReadOnly(true);
        }

        if (formCtrlDef.configData) {
            //Update used/available based on configData
            self.setValue(formCtrlDef.configData);
            $timeout(function () {
                loadedDeferred.resolve();
            });
        } else {
            loadedDeferred.resolve();
        }
    };
    this.$compiledAndLinked.$inject = ["$scope", "$timeout"];

    /********************************************************************************/
    /*** END Angular/Render Work                                                  ***/
    /********************************************************************************/
};
//Make this API inherit from EventEmitter
util.inherits(module.exports, EventEmitter);

//Tell the Form renderer that this is an Angular Control.
module.exports.isAngularControl = true;

//Tell the Form renderer that this adapter supports multi edit mode.
module.exports.isMultiEditSupported = true;
